import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Center,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  InputGroup,
  Spinner,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { MultiSelect } from 'react-multi-select-component';
import { Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FaInfoCircle } from 'react-icons/fa';
import { AiFillInfoCircle, FiTrash } from 'react-icons/all';
import Select from 'react-select';
import { Button, Datepicker, Heading, Input, Table } from '~/components';
import { SelectItem } from '~/types';
import { MultiSelectStrings } from '~/utils/multiSelectStrings';
import {
  useEligibleContracts,
  useGroupZeroCarbonProjects,
} from '~/hooks/useZeroCarbon/useGroupZeroCarbon';
import {
  adicionarCertificado,
  postPreviewAddContract,
  solicitarCertificado,
} from '~/services/GroupZeroCarbon';
import {
  PreviewAddContractView,
  ZeroCarbonProjectView,
} from '~/types/zeroCarbon';
import { queryClient } from '~/utils/react-query';
import { formatCurrency } from '~/utils/formatCurrency';
import { FileUploader } from '~/components/FileInput';

const AddCertificateFormSchema = yup.object().shape({
  codigo_certificado: yup
    .string()
    .required('Código do certificado obrigatório')
    .max(255, 'Máximo de 255 caracteres'),
  link_certificado: yup
    .string()
    .url('Link inválido')
    .required('Link do certificado obrigatório')
    .max(255, 'Máximo de 255 caracteres'),
  anexo_certificadora_nome: yup
    .string()
    .required('Anexo obrigatório')
    .max(100, 'Máximo de 100 caracteres'),
  anexo_validadora_nome: yup
    .string()
    .required('Anexo obrigatório')
    .max(100, 'Máximo de 100 caracteres'),
  contratos: yup.array().min(1, 'Selecione ao menos um contrato'),
  projeto: yup.string().required('Projeto obrigatório'),
  data_certificacao: yup
    .date()
    .required('Data de certificação obrigatória')
    .typeError('Data de certificação obrigatória'),
});

interface Certificado {
  contratos: SelectItem[];
  projeto: string;
  codigo_certificado: string;
  link_certificado: string;
  anexo_certificadora_file: File;
  anexo_certificadora_nome: string;
  anexo_validadora_file: File;
  anexo_validadora_nome: string;
  data_certificacao: Date;
}

export const AddCertificateDialog: React.FC<{ id_grupo: string }> = ({
  id_grupo,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { formState, getValues, setValue, setError, clearErrors } = useForm({
    resolver: yupResolver(AddCertificateFormSchema),
  });
  const { errors } = formState;
  const certificate = {
    contratos: [],
    projeto: '',
    codigo_certificado: '',
    link_certificado: '',
    anexo_certificadora_file: {} as File,
    anexo_certificadora_nome: '',
    anexo_validadora_file: {} as File,
    anexo_validadora_nome: '',
    data_certificacao: new Date(),
  } as Certificado;
  const [certificado, setCertificado] = useState<Certificado>(certificate);
  const { data: projects, refetch: refetchProjects } =
    useGroupZeroCarbonProjects();
  const {
    data: contracts,
    refetch: refetchContracts,
    isLoading: isLoadingContracts,
  } = useEligibleContracts(id_grupo, {
    onSuccess: data => {
      setListaContratos(
        data.results.map(contract => ({
          label: `${contract.nu_contrato} | ${
            contract.nu_pedido
          } | Saldo a compensar: ${formatCurrency(
            Number(contract.saldo_co2_kg),
          ).replace('R$', '')}kg`,
          value: `${contract.id_contrato}`,
        })),
      );
    },
  });
  const [listaContratos, setListaContratos] = useState<SelectItem[]>([]);
  const [contratosSelecionados, setContratosSelecionados] = useState<
    SelectItem[]
  >([]);
  const [previewContratos, setPreviewContratos] = useState<
    PreviewAddContractView | undefined
  >(undefined);
  const [projetoSelecionado, setProjetoSelecionado] = useState<string>('');
  const [isFetching, setIsFetching] = useState(false);

  const handleClose = () => {
    setContratosSelecionados([]);
    setProjetoSelecionado('');
    setPreviewContratos(undefined);
    setCertificado(certificate);
    clearErrors();
    onClose();
  };

  const handleOpen = () => {
    onOpen();
    setValue('contratos', []);
    setValue('projeto', '');
    refetchContracts();
    refetchProjects();
  };

  useEffect(() => {
    const ctt = contracts?.results.filter(x => {
      return contratosSelecionados.some(y => Number(y.value) === x.id_contrato);
    });
    const fctt = ctt?.map(x => {
      return {
        id_contrato: x.id_contrato,
        id_adendoaditivo: x.id_adendoaditivo,
      };
    });
    postPreviewAddContract(fctt || [])
      .then(data => {
        setPreviewContratos(data);
      })
      .catch(err => {
        toast({
          title: 'Erro',
          description: err.response.data.detail,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      });
  }, [contratosSelecionados]);

  const formInvalido = () => {
    let temErros = false;
    try {
      AddCertificateFormSchema.validateSync(certificado, {
        abortEarly: false,
      });
      clearErrors();
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        err.inner.forEach(e => {
          setError(e.path!, { message: e.message });
        });
        temErros = true;
      }
    }
    if (certificado.anexo_validadora_file.size > 5 * 1024 * 1024) {
      setError('anexo_validadora_nome', {
        type: 'maxSize',
        message: 'O arquivo deve ter no máximo 5MB',
      });
      temErros = true;
    }
    if (certificado.anexo_certificadora_file.size > 5 * 1024 * 1024) {
      setError('anexo_certificadora_nome', {
        type: 'maxSize',
        message: 'O arquivo deve ter no máximo 5MB',
      });
      temErros = true;
    }
    return temErros;
  };

  const salvar = () => {
    if (formInvalido()) {
      toast({
        title: 'Erro',
        description: 'O formulário apresenta erros.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    const formData = new FormData();
    formData.append('codigo_certificado', certificado.codigo_certificado);
    formData.append('link_certificado', certificado.link_certificado);
    formData.append(
      'data_certificacao',
      certificado.data_certificacao.toISOString().split('T')[0],
    );
    formData.append(
      'anexo_certificado_certificadora',
      certificado.anexo_certificadora_file,
    );
    formData.append(
      'anexo_certificado_validadora',
      certificado.anexo_validadora_file,
    );
    const ctts = certificado.contratos.map(x => ({
      id_contrato: Number(x.value),
      id_adendoaditivo:
        contracts?.results.find(y => y.id_contrato === Number(x.value))
          ?.id_adendoaditivo || undefined,
    }));
    formData.append('contratos', JSON.stringify(ctts));
    formData.append('id_projeto_co2', certificado.projeto);
    formData.append('id_grupo_economico', id_grupo);

    setIsFetching(true);
    adicionarCertificado(formData)
      .then(() => {
        toast({
          title: 'Certificado adicionado com sucesso',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries([
          'group_certificates_zero_carbon',
          id_grupo,
        ]);
        queryClient.invalidateQueries([
          'group_zero_carbon_statistics',
          id_grupo,
        ]);
        handleClose();
      })
      .catch(err => {
        toast({
          title: 'Erro ao adicionar certificado',
          description: err.response.data.detail,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsFetching(false);
      });
  };

  const handleSelectedContractChange = (changes: SelectItem[]) => {
    clearErrors('contratos');
    if (changes.length === 0) {
      setError('contratos', { message: 'Selecione ao menos um contrato' });
    }
    setValue('contratos', changes);
    setContratosSelecionados(changes);
    setCertificado({
      ...certificado,
      contratos: changes,
    });
  };

  const removeSelecionado = (id: number) => {
    const newContratosSelecionados = contratosSelecionados.filter(
      x => Number(x.value) !== id,
    );
    if (newContratosSelecionados.length === 0) {
      setError('contratos', { message: 'Selecione ao menos um contrato' });
    }
    setValue('contratos', newContratosSelecionados);
    setContratosSelecionados(newContratosSelecionados);
    setCertificado({
      ...certificado,
      contratos: newContratosSelecionados,
    });
  };

  const selectedProject = projects?.find(
    x => x.id === Number(projetoSelecionado),
  );

  const templateOptions = (project: ZeroCarbonProjectView) => {
    return {
      value: project.id,
      label: (
        <HStack justifyContent="space-between" mb={1}>
          <Stack>
            <Text title={project.nome} m={0} p={0} lineHeight={1}>
              {project.nome.length > 80
                ? `${project.nome.slice(0, 80)}...`
                : project.nome}
            </Text>
            <Text
              color="blackAlpha.600"
              fontSize="13px"
              m={0}
              p={0}
              lineHeight={1}
            >
              <b>Quantidade de CO2e disponível:</b>{' '}
              {project.formattedQuantidadeCo2Disponivel} tCO2e
            </Text>
          </Stack>
          {previewContratos &&
            Number(previewContratos.total_a_ser_compensado_t) >
              Number(project.quantidade_co2_disponivel) && (
              <HStack color="red.500" gridGap={2} whiteSpace="nowrap">
                <Icon as={AiFillInfoCircle} />
                <Text>Projeto sem crédito CO2e suficiente</Text>
              </HStack>
            )}
        </HStack>
      ),
    };
  };

  const options = projects?.map(projeto =>
    templateOptions(projeto as ZeroCarbonProjectView),
  );

  const value = () => {
    const projeto = projects?.find(x => x.id === Number(projetoSelecionado));
    return projeto
      ? templateOptions(projeto as ZeroCarbonProjectView)
      : undefined;
  };

  const limparAnexo = (tipo: 'certificadora' | 'validadora') => {
    if (tipo === 'certificadora') {
      setCertificado({
        ...certificado,
        anexo_certificadora_file: {} as File,
        anexo_certificadora_nome: '',
      });
    } else {
      setCertificado({
        ...certificado,
        anexo_validadora_file: {} as File,
        anexo_validadora_nome: '',
      });
    }
  };

  return (
    <>
      <Button variant="secondary" size="sm" onClick={handleOpen}>
        Adicionar certificado
      </Button>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="xl"
        closeOnOverlayClick={false}
      >
        <DrawerOverlay />
        <DrawerContent sx={{ maxWidth: '75vw', width: '75vw' }}>
          <DrawerCloseButton />
          <DrawerHeader mb={4}>
            <Heading fontSize="xl">Adicionar certificado</Heading>
          </DrawerHeader>

          <DrawerBody>
            <Stack gridGap={3}>
              <Stack>
                <Heading fontSize="md">Contratos vinculados</Heading>
                <Text fontSize="13px" color="blackAlpha.500">
                  Selecionar apenas os contratos que já tiveram emissão de
                  certificados.
                </Text>
              </Stack>
              <FormControl width="100%" isInvalid={errors.contratos}>
                <FormLabel fontSize="14px">
                  Contratos <span style={{ color: 'red' }}>*</span>
                </FormLabel>
                <InputGroup
                  border={errors.contratos ? '1px solid red' : ''}
                  borderRadius="4px"
                  display="block"
                >
                  <MultiSelect
                    value={certificado.contratos}
                    isLoading={isLoadingContracts}
                    options={listaContratos}
                    onChange={(changes: SelectItem[]) => {
                      clearErrors('contratos');
                      handleSelectedContractChange(changes);
                      if (changes.length === 0) {
                        setError('contratos', {
                          message: 'Selecione ao menos um contrato',
                        });
                      }
                    }}
                    labelledBy="Selecione"
                    overrideStrings={{
                      ...MultiSelectStrings,
                      selectAll: 'Contrato | Termo/Pedido de Locação',
                    }}
                    disabled={isFetching}
                  />
                </InputGroup>
                <FormErrorMessage>{errors.contratos?.message}</FormErrorMessage>
              </FormControl>
              <TableContainer>
                <Table type="secondary">
                  <Thead>
                    <Tr>
                      <Th>N° CONTRATO SAP</Th>
                      <Th>N° DO TERMO</Th>
                      <Th textAlign="end">QTDE DE ITENS</Th>
                      <Th textAlign="end">PRAZO</Th>
                      <Th textAlign="end">
                        <HStack textAlign="end">
                          <Text>CONSUMO INICIAL (CO2E)</Text>
                          <Tooltip
                            hasArrow
                            label="Consumo inicial (kg CO2e) * Quantidade de equipamentos"
                            bg="black"
                            rounded="md"
                            placement="top"
                            shouldWrapChildren
                          >
                            <Icon
                              as={FaInfoCircle}
                              boxSize={4}
                              color="gray.900"
                            />
                          </Tooltip>
                        </HStack>
                      </Th>
                      <Th textAlign="end">
                        <HStack textAlign="end">
                          <Text>CONSUMO MENSAL (CO2E)</Text>
                          <Tooltip
                            hasArrow
                            label="Consumo mensal (kg CO2e/mês) * Quantidade de equipamentos"
                            bg="black"
                            rounded="md"
                            placement="top"
                            shouldWrapChildren
                          >
                            <Icon
                              as={FaInfoCircle}
                              boxSize={4}
                              color="gray.900"
                            />
                          </Tooltip>
                        </HStack>
                      </Th>
                      <Th textAlign="end">
                        <HStack justifyContent="end">
                          <Text>CONSUMO TOTAL (CO2E)</Text>
                          <Tooltip
                            hasArrow
                            label="Consumo inicial + (Consumo mensal * Prazo)"
                            bg="black"
                            rounded="md"
                            placement="top"
                            shouldWrapChildren
                          >
                            <Icon
                              as={FaInfoCircle}
                              boxSize={4}
                              color="gray.900"
                            />
                          </Tooltip>
                        </HStack>
                      </Th>
                      <Th textAlign="end">SALDO A COMPENSAR</Th>
                      <Th>AÇÃO</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {!!previewContratos && previewContratos.preview.length ? (
                      previewContratos.preview.map(it => (
                        <Tr key={it.nu_contrato}>
                          <Td>{it.nu_contrato}</Td>
                          <Td>{it.nu_pedido}</Td>
                          <Td textAlign="end">{it.quantidade_itens}</Td>
                          <Td textAlign="end">{it.prazo}</Td>
                          <Td textAlign="end">
                            {it.formattedConsumoInicialKg}kg
                          </Td>
                          <Td textAlign="end">
                            {it.formattedConsumoMensalKg}kg
                          </Td>
                          <Td textAlign="end">
                            {it.formattedConsumoTotalKg}kg
                          </Td>
                          <Td textAlign="end">
                            {it.formattedConsumoDisponivelKg}kg
                          </Td>
                          <Td>
                            <IconButton
                              onClick={() => removeSelecionado(it.id_contrato)}
                              aria-label="Remover"
                              icon={<FiTrash />}
                              isDisabled={isFetching}
                            />
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={7} textAlign="center">
                          {' '}
                          Nenhum contrato selecionado.
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
              <HStack
                gridGap={5}
                divider={
                  <Center height="60px">
                    <Divider orientation="vertical" />
                  </Center>
                }
              >
                <Stack gridGap={2} w="30%">
                  <Text fontSize="15px" fontWeight="bold">
                    Total consumido:
                  </Text>
                  <HStack alignItems="baseline" gridGap={0}>
                    <Heading>
                      {previewContratos?.formattedTotalConsumidoKg || '- '}kg
                    </Heading>
                  </HStack>
                </Stack>
                <Stack gridGap={2} w="30%">
                  <Text fontSize="15px" fontWeight="bold">
                    Total a ser compensado:
                  </Text>
                  <HStack alignItems="baseline" gridGap={0}>
                    <Heading>
                      {previewContratos?.formattedTotalASerCompensadoT || '- '}t
                    </Heading>
                  </HStack>
                </Stack>
                <Stack gridGap={2} w="30%">
                  <Text fontSize="15px" fontWeight="bold">
                    Saldo:
                  </Text>
                  <HStack alignItems="baseline" gridGap={0}>
                    <Heading>
                      {previewContratos?.formattedSaldoCo2Kg || '- '}kg
                    </Heading>
                  </HStack>
                </Stack>
              </HStack>
              <FormControl width="100%" isInvalid={errors.projeto}>
                <FormLabel fontSize="14px">
                  Projetos <span style={{ color: 'red' }}>*</span>
                </FormLabel>
                <Select
                  value={value()}
                  options={options}
                  placeholder="Selecione"
                  onChange={e => {
                    clearErrors('projeto');
                    !e?.value &&
                      setError('projeto', { message: 'Projeto obrigatório' });
                    setCertificado({
                      ...certificado,
                      projeto: e ? e.value.toString() : '',
                    });
                  }}
                  isDisabled={isFetching}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: errors.projeto ? 'red' : '',
                    }),
                  }}
                />
                <FormErrorMessage>{errors.projeto?.message}</FormErrorMessage>
              </FormControl>
              {!!projetoSelecionado && !selectedProject?.possui_carta && (
                <Alert
                  bg="#f7f2fa"
                  borderRadius="md"
                  color="#9246ce"
                  fontSize="sm"
                  py={1}
                  px={2}
                >
                  <AlertDescription>
                    <b>Atenção:</b> Não existe{' '}
                    <b>&quot;Carta Zero Carbon&quot;</b> cadastrada para esse
                    projeto. Cadastre antes de enviar o certificado para o
                    cliente.
                  </AlertDescription>
                </Alert>
              )}
            </Stack>
            <Stack gridGap={3} mt="40px" gridRowGap={3}>
              <Heading fontSize="md">Certificado da Certificadora</Heading>
              <Input
                value={certificado.codigo_certificado}
                name="codigo"
                type="text"
                isRequired
                label="Código do certificado"
                error={errors.codigo_certificado}
                onChange={e => {
                  clearErrors('codigo_certificado');
                  setCertificado({
                    ...certificado,
                    codigo_certificado: e.target.value,
                  });
                }}
                isDisabled={isFetching}
              />
              <FormControl>
                <FormLabel fontSize="14px" mb={2}>
                  Link do certificado da Certificadora{' '}
                  <span style={{ color: 'red' }}>*</span>
                </FormLabel>
                <InputGroup size="md">
                  <Input
                    value={certificado.link_certificado}
                    name="link"
                    type="text"
                    isRequired
                    error={errors.link_certificado}
                    onChange={e => {
                      clearErrors('link_certificado');
                      setCertificado({
                        ...certificado,
                        link_certificado: e.target.value,
                      });
                    }}
                    isDisabled={isFetching}
                  />
                </InputGroup>
              </FormControl>
              <HStack>
                <Flex width="45%">
                  <Datepicker
                    label="Data de certificação"
                    required
                    onChange={e => {
                      clearErrors('data_certificacao');
                      setCertificado({
                        ...certificado,
                        data_certificacao: e,
                      });
                    }}
                    maxDate={new Date()}
                    name="data_certificacao"
                    isClearable={!isFetching}
                    selectedDate={certificado.data_certificacao}
                    error={errors.data_certificacao}
                    isDisabled={isFetching}
                  />
                </Flex>
                <FileUploader
                  isRequired
                  fileName={certificado.anexo_certificadora_nome}
                  onChange={file => {
                    clearErrors('anexo_certificadora_nome');
                    setCertificado({
                      ...certificado,
                      anexo_certificadora_nome: file.name,
                      anexo_certificadora_file: file,
                    });
                  }}
                  isDisabled={isFetching}
                  clearFile={() => {
                    limparAnexo('certificadora');
                  }}
                  label="Anexar certificado da Certificadora"
                  showButton={false}
                  errors={errors.anexo_certificadora_nome}
                  helpText="Arquivo do tipo pdf. O limite total dos arquivos é de 5MB."
                />
              </HStack>
            </Stack>
            <Stack gridGap={3} mt="40px" gridRowGap={3}>
              <Heading fontSize="md">Certificado da Validadora</Heading>
              <FileUploader
                isRequired
                fileName={certificado.anexo_validadora_nome}
                onChange={file => {
                  clearErrors('anexo_validadora_nome');
                  setCertificado({
                    ...certificado,
                    anexo_validadora_nome: file.name,
                    anexo_validadora_file: file,
                  });
                }}
                isDisabled={isFetching}
                clearFile={() => {
                  limparAnexo('validadora');
                }}
                label="Anexar certificado"
                showButton={false}
                errors={errors.anexo_validadora_nome}
                helpText="Arquivo do tipo pdf. O limite total dos arquivos é de 5MB."
              />
            </Stack>
          </DrawerBody>

          <DrawerFooter>
            <Button variant="secondary" onClick={() => handleClose()}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={salvar}
              ms={2}
              isDisabled={isFetching}
            >
              Salvar {isFetching && <Spinner />}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};
